import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, createSelector } from '@reduxjs/toolkit';
import type { RootState } from './Store';
import {
  makeRootVariableNode,
  toVariableEntities,
  isVariableFolder,
  makeFolderNode,
  makeUserLeafNode,
  type VariableEntity,
  type VariableNode,
  type VariableShape,
} from 'services/entities/VariablesEntity';
import { traverseTree } from '@proposify/frontend-utils/traverse';
import { findTreeNode } from '@proposify/frontend-utils/findTreeNode';

export interface ApiError {
  message: string;
  code: number;
}

export interface VariablesState {
  data: VariableNode;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: ApiError | null;
  crmIsError?: boolean;
}

const initialState: VariablesState = {
  data: makeRootVariableNode(),
  status: 'idle',
  error: null,
  crmIsError: false,
};

const humanize = (s: string): string => s.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const allVariableSlice = createSlice({
  name: 'variables',
  initialState,
  reducers: {
    updateVariableValue: (state, action: PayloadAction<{ variableName: VariableNode['name']; newValue: VariableNode['value'] }>) => {
      const { variableName, newValue } = action.payload;
      const variable = findTreeNode(state.data, (n) => n.name === variableName, 'variables');
      if (variable) {
        variable.value = newValue;
      }
    },
    upsertVariableValue: (
      state,
      action: PayloadAction<{
        shape?: VariableShape;
        variableName: VariableNode['name'];
        newValue: VariableNode['value'];
        parent: VariableNode['name'];
      }>
    ) => {
      const { shape, variableName, newValue, parent } = action.payload;
      const existing = findTreeNode(state.data, (n) => n.name === variableName, 'variables');
      if (existing) {
        existing.value = newValue;
        return;
      }
      state.data.variables = state.data.variables ?? [];
      let parentFolder = findTreeNode(state.data, (n) => n.name === parent && isVariableFolder(n), 'variables');
      if (!parentFolder) {
        parentFolder = makeFolderNode({ name: parent, friendlyName: humanize(parent), path: parent, variables: [] });
        state.data.variables.push(parentFolder);
      }
      parentFolder.variables = parentFolder.variables ?? [];
      parentFolder.variables.push(
        makeUserLeafNode({
          name: variableName,
          friendlyName: humanize(variableName),
          shape,
          value: newValue,
          path: `${parentFolder.path}/${variableName}`,
        })
      );
    },
    setVariables: (state, action: PayloadAction<VariableNode>) => {
      state.data = action.payload;
      state.status = 'succeeded';
      state.error = null;
    },
    setCrmIsError: (state, action: PayloadAction<boolean>) => {
      state.crmIsError = action.payload;
    },
  },
});

export const selectAllVariables = (state: RootState) => state.allVariables.data;
export const selectVariablesStatus = (state: RootState) => state.allVariables.status;
export const selectCrmVariablesIsError = (state: RootState) => state.allVariables.crmIsError ?? false;
export const selectAllVariablesItems = createSelector(selectAllVariables, (node): VariableEntity[] => {
  if (!node) return [];
  return toVariableEntities(node);
});

export const selectFlatVariableNodes = createSelector(selectAllVariables, (node): VariableNode[] => {
  const nodes: VariableNode[] = [];
  if (!node) return nodes;
  traverseTree(node, (n) => nodes.push(n), 'variables');
  return nodes;
});

export const selectVariablesByName = createSelector(selectAllVariables, (node): Record<string, VariableNode> => {
  const map: Record<string, VariableNode> = {};
  if (!node) return map;
  traverseTree(
    node,
    (n) => {
      map[n.name] = n;
    },
    'variables'
  );
  return map;
});

export const selectVariableByName = (state: RootState, name: string): VariableNode | undefined => selectVariablesByName(state)[name];

// Keyed by `${name}::${objectId}` so array-instance leaves that share a `name`
// (DT-1886) resolve to the exact instance a chip was stamped from, instead of
// the last-indexed sibling. Folders and other nodes without an objectId are
// skipped — they stay resolvable only through selectVariablesByName.
export const selectVariablesByNameAndObjectId = createSelector(selectAllVariables, (node): Record<string, VariableNode> => {
  const map: Record<string, VariableNode> = {};
  if (!node) return map;
  traverseTree(
    node,
    (n) => {
      if (n.objectId) {
        map[`${n.name}::${n.objectId}`] = n;
      }
    },
    'variables'
  );
  return map;
});

export const selectVariablesByTemplate = createSelector(selectAllVariables, (node): Record<string, VariableNode> => {
  const map: Record<string, VariableNode> = {};
  if (!node) return map;
  traverseTree(
    node,
    (n) => {
      if (n.template) map[n.template] = n;
    },
    'variables'
  );
  return map;
});

export const selectVariablesByTemplateAndObjectId = createSelector(selectAllVariables, (node): Record<string, VariableNode> => {
  const map: Record<string, VariableNode> = {};
  if (!node) return map;
  traverseTree(
    node,
    (n) => {
      if (n.template && n.objectId) {
        map[`${n.template}::${n.objectId}`] = n;
      }
    },
    'variables'
  );
  return map;
});

export const selectVariableForChip = (
  state: RootState,
  attrs: { name: string; objectId?: string | null; template?: string | null }
): VariableNode | undefined => {
  if (attrs.objectId && attrs.template) {
    const byTemplate = selectVariablesByTemplateAndObjectId(state)[`${attrs.template}::${attrs.objectId}`];
    if (byTemplate) return byTemplate;
  }
  if (attrs.objectId) {
    return selectVariablesByNameAndObjectId(state)[`${attrs.name}::${attrs.objectId}`];
  }
  return selectVariableByName(state, attrs.name);
};

export const { updateVariableValue, upsertVariableValue, setVariables, setCrmIsError } = allVariableSlice.actions;

export const allVariables = allVariableSlice.reducer;
